import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/admin/DashboardPage';
import CashierDashboardPage from './pages/cashier/CashierDashboardPage';
import TransactionsPage from './pages/admin/TransactionsPage';
import TransactionDetailPage from './pages/TransactionDetailPage';
import NewTransactionPage from './pages/NewTransactionPage';
import DebtPage from './pages/admin/DebtPage';
import ReportsPage from './pages/admin/ReportsPage';
import AdminUsersPage from './pages/admin/UsersPage';
import AdminEntitiesPage from './pages/admin/EntitiesPage';
import AdminWalletsPage from './pages/admin/WalletsPage';
import AdminPeriodsPage from './pages/admin/PeriodsPage';
import AdminCategoriesPage from './pages/admin/CategoriesPage';
import AdminCurrenciesPage from './pages/admin/CurrenciesPage';
import AdminClientsPage from './pages/admin/ClientsPage';
import AdminDailyBalancesPage from './pages/admin/DailyBalancesPage';
import AdminLogsPage from './pages/admin/LogsPage';
import AdminCashDesksPage from './pages/admin/CashDesksPage';
import ReconciliationPage from './pages/admin/ReconciliationPage';
import CashierTransactionsPage from './pages/cashier/CashierTransactionsPage';
import CashierDebtPage from './pages/cashier/CashierDebtPage';
import CashierWalletsPage from './pages/cashier/CashierWalletsPage';
import CashierReportsPage from './pages/cashier/CashierReportsPage';
import CashierClientsPage from './pages/cashier/CashierClientsPage';
import CashierMovementsPage from './pages/cashier/CashierMovementsPage';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <div className="loading">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  if (user?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function DashboardRouter() {
  const { user } = useAuth();

  // Show cashier-specific dashboard for cashier role
  if (user?.role === 'cashier') {
    return <CashierDashboardPage />;
  }

  // Default dashboard for other roles
  return <DashboardPage />;
}

function TransactionsRouter() {
  const { user } = useAuth();

  if (user?.role === 'cashier') {
    return <CashierTransactionsPage />;
  }

  return <TransactionsPage />;
}

function DebtRouter() {
  const { user } = useAuth();

  if (user?.role === 'cashier') {
    return <CashierDebtPage />;
  }

  return <DebtPage />;
}

function WalletsRouter() {
  const { user } = useAuth();

  if (user?.role === 'cashier') {
    return <CashierWalletsPage />;
  }

  return <AdminWalletsPage />;
}

function ReportsRouter() {
  const { user } = useAuth();

  if (user?.role === 'cashier') {
    return <CashierReportsPage />;
  }

  return <ReportsPage />;
}

function ClientsRouter() {
  const { user } = useAuth();

  if (user?.role === 'cashier') {
    return <CashierClientsPage />;
  }

  // For non-cashier users, redirect to dashboard (or create a separate admin clients page if needed)
  return <Navigate to="/" replace />;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      
      <Route path="/" element={
        <PrivateRoute>
          <Layout />
        </PrivateRoute>
      }>
        <Route index element={<DashboardRouter />} />
        <Route path="transactions" element={<TransactionsRouter />} />
        <Route path="transactions/new" element={<NewTransactionPage />} />
        <Route path="transactions/:id" element={<TransactionDetailPage />} />
        <Route path="debt" element={<DebtRouter />} />
        <Route path="wallets" element={<WalletsRouter />} />
        <Route path="reports" element={<ReportsRouter />} />
        <Route path="clients" element={<ClientsRouter />} />
        <Route path="movements" element={<CashierMovementsPage />} />

        <Route path="admin/users" element={<AdminRoute><AdminUsersPage /></AdminRoute>} />
        <Route path="admin/entities" element={<AdminRoute><AdminEntitiesPage /></AdminRoute>} />
        <Route path="admin/wallets" element={<WalletsRouter />} />
        <Route path="admin/periods" element={<AdminPeriodsPage />} />
        <Route path="admin/categories" element={<AdminCategoriesPage />} />
        <Route path="admin/currencies" element={<AdminRoute><AdminCurrenciesPage /></AdminRoute>} />
        <Route path="admin/clients" element={<AdminRoute><AdminClientsPage /></AdminRoute>} />
        <Route path="admin/daily-balances" element={<AdminDailyBalancesPage />} />
        <Route path="admin/logs" element={<AdminRoute><AdminLogsPage /></AdminRoute>} />
        <Route path="admin/cash-desks" element={<AdminRoute><AdminCashDesksPage /></AdminRoute>} />
        <Route path="admin/wallets/:walletId/reconciliation" element={<AdminRoute><ReconciliationPage /></AdminRoute>} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
